import { i18n } from 'boot/i18n';
import { notifyNegative } from 'src/composables/interactions';
import { logEvent } from 'src/utils/LogEvent';

export function isQuotaExceededError(err) {
  if (!err) {
    return false;
  }

  // Dexie wraps the original DOM exception into `inner`, and GenericIndexedDb wraps the Dexie
  // error the same way, so the name has to be looked up along the whole chain
  for (let current = err, depth = 0; current && depth < 5; current = current.inner, depth += 1) {
    if (current.name === 'QuotaExceededError') {
      return true;
    }
  }

  const message = typeof err === 'string' ? err : (err.message || '');
  return /quota(?:.{0,20})?exceeded|exceeded(?:.{0,20})?quota|maximum size|disk is full/i.test(message);
}

export async function getStorageEstimate() {
  try {
    if (!navigator.storage?.estimate) {
      return null;
    }
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    const percentUsed = quota ? Math.round((usage / quota) * 100) : 0;
    return { usage, quota, percentUsed };
  } catch (err) {
    return null;
  }
}

const QUOTA_THROTTLE_MS = 60 * 1000;

const quotaEventLoggedAt = new Map();
let quotaNotifyShownAt = 0;

/**
 * Quota errors arrive in bursts (one per failed write), so the Sentry event is throttled to one
 * per minute per collection - throttling it globally would hide every collection but the first.
 */
export async function logStorageQuotaEvent(message, contextData = {}) {
  const throttleKey = contextData.collectionPath || 'unknown';
  const now = Date.now();
  if (now - (quotaEventLoggedAt.get(throttleKey) || 0) < QUOTA_THROTTLE_MS) {
    return;
  }
  quotaEventLoggedAt.set(throttleKey, now);

  const estimate = await getStorageEstimate();
  logEvent(message, {
    tags: { quotaExceeded: true },
    context: {
      context_data: contextData,
      storage_estimate: estimate || { unavailable: true },
    },
  });
}

/**
 * One notification per minute is enough - the user can only free up space once.
 */
export function notifyStorageQuotaExceeded() {
  const now = Date.now();
  if (now - quotaNotifyShownAt < QUOTA_THROTTLE_MS) {
    return;
  }
  quotaNotifyShownAt = now;

  notifyNegative(i18n.t('common.storageQuotaExceededTitle'), {
    caption: i18n.t('common.storageQuotaExceededMessage'),
    multiLine: true,
  });
}
